import React from 'react';

import { faMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { allowedTimesMessages } from 'src/features/allowedTimes/utils/messages';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/features/sites/utils/constants';
import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/workingHours/utils/constants';
import { CustomWorkingHoursFormSchemaValues } from 'src/features/workingHours/utils/formSchema';
import { workingHoursMessages } from 'src/features/workingHours/utils/messages';
import { DAY_OF_WEEK } from 'src/features/workingHours/utils/types';
import { useListZones } from 'src/features/zone/api/useListZones';
import { useParking } from 'src/providers/ParkingProvider';
import MultiSelectFormField from 'src/registry/components/forms/MultiSelectFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { translate } from 'src/utils/translate';

type WorkingHoursFormProps = {
    form: UseFormReturn<CustomWorkingHoursFormSchemaValues>;
    onSubmit: (values: CustomWorkingHoursFormSchemaValues) => void;
    isCreate: boolean;
};

function WorkingHoursForm({ onSubmit, form, isCreate }: WorkingHoursFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: zonesData } = useListZones({
        parkingId,
    });

    const { all_day } = form.watch();

    const zoneOptions = React.useMemo(() => {
        if (!zonesData) return [];
        return zonesData.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [zonesData]);

    const options = React.useMemo(
        () => [
            {
                id: DAY_OF_WEEK.MONDAY,
                name: formatMessage(workingHoursMessages.monday),
            },
            {
                id: DAY_OF_WEEK.TUESDAY,
                name: formatMessage(workingHoursMessages.tuesday),
            },
            {
                id: DAY_OF_WEEK.WEDNESDAY,
                name: formatMessage(workingHoursMessages.wednesday),
            },
            {
                id: DAY_OF_WEEK.THURSDAY,
                name: formatMessage(workingHoursMessages.thursday),
            },
            {
                id: DAY_OF_WEEK.FRIDAY,
                name: formatMessage(workingHoursMessages.friday),
            },
            {
                id: DAY_OF_WEEK.SATURDAY,
                name: formatMessage(workingHoursMessages.saturday),
            },
            {
                id: DAY_OF_WEEK.SUNDAY,
                name: formatMessage(workingHoursMessages.sunday),
            },
        ],
        [formatMessage],
    );

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <SelectFormField<CustomWorkingHoursFormSchemaValues>
                form={form}
                name="zone"
                label={formatMessage(workingHoursMessages.zone)}
                options={zoneOptions}
            />
            {isCreate ? (
                <MultiSelectFormField<CustomWorkingHoursFormSchemaValues>
                    form={form}
                    required
                    name="day_of_week"
                    label={formatMessage(allowedTimesMessages.dayOfWeek)}
                    options={options}
                />
            ) : (
                <SelectFormField<CustomWorkingHoursFormSchemaValues>
                    form={form}
                    required
                    name="day_of_week"
                    label={formatMessage(workingHoursMessages.weekday)}
                    options={Object.values(DAY_OF_WEEK)}
                    optionTranslationMap={DAY_OF_WEEK_TRANSLATIONS_MAP}
                />
            )}
            <SelectFormField<CustomWorkingHoursFormSchemaValues>
                form={form}
                required
                name="all_day"
                isBoolean
                label={formatMessage(workingHoursMessages.allDay)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
                onChange={(value) => {
                    const isAllDay = JSON.parse(value) as boolean;
                    form.setValue('start_time', isAllDay ? null : '00:00:00');
                    form.setValue('end_time', isAllDay ? null : '23:59:59');
                }}
            />
            <FormField
                control={form.control}
                name="start_time"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(workingHoursMessages.timeFrame)}</FormLabel>
                        <div className="flex w-full items-center gap-4">
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        disabled={all_day}
                                        type="time"
                                        className="w-full"
                                        step={1}
                                        {...field}
                                        value={field.value || '00:00:00'}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                            <FontAwesomeIcon className="h-10 w-3 text-sm text-gray-400" icon={faMinus} />
                            <FormField
                                control={form.control}
                                name="end_time"
                                render={({ field }) => (
                                    <FormControlStack>
                                        <FormControl>
                                            <Input
                                                disabled={all_day}
                                                type="time"
                                                className="w-full"
                                                step={1}
                                                {...field}
                                                value={field.value || '23:59:59'}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                )}
                            />
                        </div>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default WorkingHoursForm;
